export interface LocalFileMetadata {
  name: string;
  size: number;
  type: string;
  isMetadata: true;
  status: "local_file_needs_reselect";
}

export interface SerializedPendingMediaItem {
  supportImageFile: LocalFileMetadata | null;
  audioFile: LocalFileMetadata | null;
  optionImages: {
    A: LocalFileMetadata | null;
    B: LocalFileMetadata | null;
    C: LocalFileMetadata | null;
    D: LocalFileMetadata | null;
  };
}

export interface ImportDraft {
  draftVersion: number;
  savedAt: string;
  updatedAt: string;
  status: "idle" | "saving" | "save_failed";
  currentStep: number;
  selectedCompetence: "Reading" | "Listening" | "Writing" | null;
  selectedTemplateId: string;
  inputText: string;
  importSubSection: string;
  isManualSubtypeSelected: boolean;
  allowPartialImport: boolean;
  blindMode: boolean;
  validationResult: any;
  pendingMedia: Record<string, SerializedPendingMediaItem>;
  manualAudioMappings: Record<string, LocalFileMetadata | null>;
  resolvedAudioMappings: Record<string, { file: LocalFileMetadata | null; method: string }>;
  audioFilesMetadata: LocalFileMetadata[];
  batchImageFilesMetadata: LocalFileMetadata[];
  selectedImageFileMetadata: LocalFileMetadata | null;
}

const DRAFT_VERSION = 1;
const EXPIRATION_DAYS = 7;

/**
 * Safely converts a File to serializable metadata
 */
export function serializeFile(file: any): LocalFileMetadata | null {
  if (!file) return null;
  // If it's already serialized metadata
  if (file.isMetadata) return file;
  
  if (file instanceof File) {
    return {
      name: file.name,
      size: file.size,
      type: file.type,
      isMetadata: true,
      status: "local_file_needs_reselect"
    };
  }
  
  return null;
}

/**
 * Strips circular structures, binary references and blob URLs from validation results
 */
function sanitizeQuestion(q: any): any {
  if (!q || typeof q !== "object") return q;
  const cleaned = { ...q };
  
  // Remove binary files
  delete cleaned.associatedFile;
  
  // Remove expired Blob/Object URLs
  if (typeof cleaned.audioUrl === "string" && cleaned.audioUrl.startsWith("blob:")) {
    cleaned.audioUrl = "";
  }
  if (typeof cleaned.audio === "string" && cleaned.audio.startsWith("blob:")) {
    cleaned.audio = "";
  }
  if (typeof cleaned.imageUrl === "string" && cleaned.imageUrl.startsWith("blob:")) {
    cleaned.imageUrl = "";
  }
  
  // Clean options array
  if (Array.isArray(cleaned.options)) {
    cleaned.options = cleaned.options.map((o: any) => {
      if (!o || typeof o !== "object") return o;
      const optClean = { ...o };
      if (typeof optClean.image === "string" && optClean.image.startsWith("blob:")) {
        optClean.image = "";
      }
      if (typeof optClean.imageUrl === "string" && optClean.imageUrl.startsWith("blob:")) {
        optClean.imageUrl = "";
      }
      return optClean;
    });
  }
  
  return cleaned;
}

function sanitizeValidationResult(res: any): any {
  if (!res) return null;
  const cleaned = { ...res };
  
  if (Array.isArray(cleaned.allQuestions)) {
    cleaned.allQuestions = cleaned.allQuestions.map(sanitizeQuestion);
  }
  if (Array.isArray(cleaned.validQuestions)) {
    cleaned.validQuestions = cleaned.validQuestions.map(sanitizeQuestion);
  }
  if (Array.isArray(cleaned.invalidQuestions)) {
    cleaned.invalidQuestions = cleaned.invalidQuestions.map(sanitizeQuestion);
  }
  
  return cleaned;
}

/**
 * Saves a serialized draft state to localStorage
 */
export function saveImportDraft(
  userId: string | undefined,
  state: {
    status?: "idle" | "saving" | "save_failed";
    currentStep: number;
    selectedCompetence: "Reading" | "Listening" | "Writing" | null;
    selectedTemplateId: string;
    inputText: string;
    importSubSection: string;
    isManualSubtypeSelected: boolean;
    allowPartialImport: boolean;
    blindMode: boolean;
    validationResult: any;
    pendingMedia: Record<string, any>;
    manualAudioMappings: Record<string, any>;
    resolvedAudioMappings?: Record<string, any>;
    audioFiles: any[];
    batchImageFiles: any[];
    selectedImageFile: any;
  },
  options?: { immediate?: boolean }
): void {
  const timestamp = new Date().toISOString();
  const storageKey = userId ? `tef_import_questions_draft_v1_${userId}` : "tef_import_questions_draft_v1";
  
  // Format pending media safely
  const serializedPendingMedia: Record<string, SerializedPendingMediaItem> = {};
  if (state.pendingMedia) {
    for (const [qId, media] of Object.entries(state.pendingMedia)) {
      serializedPendingMedia[qId] = {
        supportImageFile: serializeFile(media.supportImageFile),
        audioFile: serializeFile(media.audioFile),
        optionImages: {
          A: serializeFile(media.optionImages?.A),
          B: serializeFile(media.optionImages?.B),
          C: serializeFile(media.optionImages?.C),
          D: serializeFile(media.optionImages?.D)
        }
      };
    }
  }

  // Format manual mappings safely
  const serializedManualMappings: Record<string, LocalFileMetadata | null> = {};
  if (state.manualAudioMappings) {
    for (const [qId, file] of Object.entries(state.manualAudioMappings)) {
      serializedManualMappings[qId] = serializeFile(file);
    }
  }

  // Format resolved audio mappings safely (URLs are preserved, File objects become metadata)
  const serializedResolvedMappings: Record<string, { file: LocalFileMetadata | null; method: string }> = {};
  if (state.resolvedAudioMappings) {
    for (const [qId, mapping] of Object.entries(state.resolvedAudioMappings)) {
      if (mapping && typeof mapping === "object") {
        serializedResolvedMappings[qId] = {
          file: serializeFile(mapping.file),
          method: (mapping as any).method || "none"
        };
      }
    }
  }

  // Retrieve existing draft to keep savedAt if updating
  let savedAtTime = timestamp;
  try {
    const existing = localStorage.getItem(storageKey);
    if (existing) {
      const parsed = JSON.parse(existing);
      if (parsed.savedAt) savedAtTime = parsed.savedAt;
    }
  } catch (e) {
    // ignore parsing errors
  }

  const draft: ImportDraft = {
    draftVersion: DRAFT_VERSION,
    savedAt: savedAtTime,
    updatedAt: timestamp,
    status: state.status || "idle",
    currentStep: state.currentStep,
    selectedCompetence: state.selectedCompetence,
    selectedTemplateId: state.selectedTemplateId,
    inputText: state.inputText,
    importSubSection: state.importSubSection,
    isManualSubtypeSelected: state.isManualSubtypeSelected,
    allowPartialImport: state.allowPartialImport,
    blindMode: state.blindMode,
    validationResult: sanitizeValidationResult(state.validationResult),
    pendingMedia: serializedPendingMedia,
    manualAudioMappings: serializedManualMappings,
    resolvedAudioMappings: serializedResolvedMappings,
    audioFilesMetadata: (state.audioFiles || []).map(serializeFile).filter(Boolean) as LocalFileMetadata[],
    batchImageFilesMetadata: (state.batchImageFiles || []).map(serializeFile).filter(Boolean) as LocalFileMetadata[],
    selectedImageFileMetadata: serializeFile(state.selectedImageFile)
  };

  try {
    localStorage.setItem(storageKey, JSON.stringify(draft));
    console.log("[IMPORT_DRAFT_SAVE]", {
      step: draft.currentStep,
      competency: draft.selectedCompetence,
      questionCount: draft.validationResult?.totalCount || 0,
      hasRawJson: Boolean(draft.inputText),
      savedAt: draft.updatedAt,
      immediate: Boolean(options?.immediate)
    });
  } catch (err) {
    console.error("[IMPORT_DRAFT_SAVE_FAILED]", err);
  }
}

/**
 * Loads a draft from localStorage. Handles expiration and version validation.
 */
export function getImportDraft(userId: string | undefined): { draft: ImportDraft | null; reason?: string } {
  const storageKey = userId ? `tef_import_questions_draft_v1_${userId}` : "tef_import_questions_draft_v1";
  
  try {
    const serialized = localStorage.getItem(storageKey);
    if (!serialized) return { draft: null };
    
    const draft = JSON.parse(serialized) as ImportDraft;
    
    // 1. Version validation
    if (draft.draftVersion !== DRAFT_VERSION) {
      console.warn("[IMPORT_DRAFT_EXPIRED_VERSION]", {
        found: draft.draftVersion,
        expected: DRAFT_VERSION
      });
      localStorage.removeItem(storageKey);
      return { draft: null, reason: "version_mismatch" };
    }
    
    // 2. Expiration validation (7 days)
    const savedTime = new Date(draft.updatedAt || draft.savedAt).getTime();
    const nowTime = new Date().getTime();
    const diffDays = (nowTime - savedTime) / (1000 * 60 * 60 * 24);
    
    if (diffDays > EXPIRATION_DAYS) {
      console.log("[IMPORT_DRAFT_EXPIRED_TIME]", {
        ageDays: diffDays,
        limitDays: EXPIRATION_DAYS
      });
      localStorage.removeItem(storageKey);
      return { draft: null, reason: "expired" };
    }
    
    return { draft };
  } catch (err) {
    console.error("[IMPORT_DRAFT_LOAD_FAILED]", err);
    return { draft: null, reason: "corruption" };
  }
}

/**
 * Clear draft from localStorage
 */
export function clearImportDraft(userId: string | undefined, reason: string = "user_action"): void {
  const storageKey = userId ? `tef_import_questions_draft_v1_${userId}` : "tef_import_questions_draft_v1";
  localStorage.removeItem(storageKey);
  console.log("[IMPORT_DRAFT_CLEARED]", {
    reason,
    userId
  });
}
